import React, {Component} from 'react';
import PropTypes from 'prop-types';
import MoveIcon from '@common/images/move.svg';
import {Image, CloseButton} from '@learningblocks/shared-components';
import {GlossaryErrorState} from '../SharedComponents/GlossaryErrorState';
import {GlossaryDetailsLoader} from '../SharedComponents/GlossaryDetailsLoader';
import style from './style.module.scss';
import {getArrowPosition} from './HighlightModal.utils';
import {calculateTooltipPosition} from './HighlightModal.utils';
export const CONST = {
  INITIAL_TRANSFORM: 'translate3d(0,0)',
  AUTO: 'auto',
  EVENT_MOUSE_UP: 'mouseup',
  EVENT_SCROLL: 'scroll',
  EVENT_MOUSE_MOVE: 'mousemove',
  MAX_DESCRIPTION_LENGTH: 140,
  MIN_DESCRIPTION_LENGTH: 0,
};

export class HighlightModal extends Component {
  constructor(props) {
    super(props);
    this.wrapRef = React.createRef();
    this.state = {
      classList: [style.tooltip, props.className],
      startOffset: {x: null, y: null},
      onDragMode: false,
      isOnScrollMode: false,
      style: {
        left: CONST.AUTO,
        bottom: CONST.AUTO,
        top: CONST.AUTO,
        right: CONST.AUTO,
        transform: CONST.INITIAL_TRANSFORM,
      },
    };
    this.scrollingParent = null;
  }
  componentDidMount() {
    window.addEventListener(CONST.EVENT_SCROLL, this.onScroll);
    this.updateTooltipDragPosition(this.props.clickedContent);
    window.addEventListener(CONST.EVENT_MOUSE_UP, this.onDragEnd);
  }

  componentWillUnmount() {
    window.removeEventListener(CONST.EVENT_MOUSE_UP, this.onDragEnd);
    window.removeEventListener(CONST.EVENT_SCROLL, this.onScroll);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.clickedContent.top !== this.props.clickedContent.top ||
      prevProps.clickedContent.left !== this.props.clickedContent.left
    ) {
      this.setState({isOnScrollMode: false}, () => {
        this.updateTooltipDragPosition(this.props.clickedContent);
      });
    }
  }

  setNewCordinates = () => {
    if (
      this.wrapRef.current &&
      this.state.style.transform === CONST.INITIAL_TRANSFORM
    ) {
      const {position, classList} = calculateTooltipPosition(
        this.wrapRef.current
      );
      const newStyle = {
        ...this.state.style,
        ...position,
      };
      const newClassList = [style.tooltip, ...classList, this.props.className];
      this.setState({style: newStyle, classList: newClassList});
    }
  };

  updateTooltipDragPosition = (event) => {
    const {x, y} = this.state.startOffset;
    const newStyle = {...this.state.style};
    if (this.state.onDragMode) {
      newStyle.transform = `translate(${event.pageX - x}px,${
        event.pageY - y
      }px)`;
    } else if (this.state.isOnScrollMode) {
      delete newStyle.transform;
      newStyle.top = event.top - window.scrollY + event.scrollY;
      newStyle.left = event.left - window.scrollX + event.scrollX;
    } else {
      delete newStyle.transform;
      newStyle.top = event.top;
      newStyle.left = event.left;
    }
    const classList = getArrowPosition(event.arrowPosition);
    const newClassList = [style.tooltip, ...classList, this.props.className];
    this.setState({style: newStyle, classList: newClassList});
  };

  onDragStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.state.startOffset.x === null) {
      this.setState({startOffset: {x: event.pageX, y: event.pageY}});
    }
    this.setState({onDragMode: true});
    window.addEventListener(
      CONST.EVENT_MOUSE_MOVE,
      this.updateTooltipDragPosition
    );
  };
  onDragEnd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({onDragMode: false});
    window.removeEventListener(
      CONST.EVENT_MOUSE_MOVE,
      this.updateTooltipDragPosition
    );
  };
  renderImage = () => {
    if (this.props.image) {
      return <Image src={this.props.image} alt={this.props.title} />;
    } else return null;
  };

  renderTitle = () => <h4>{this.props.title}</h4>;
  closeTooltip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.close();
  };

  openGlossary = (event) => {
    this.closeTooltip(event);
    this.props.openGlossary(this.props.title);
  };
  renderDescription = () => {
    let {description} = this.props;
    if (description.length > CONST.MAX_DESCRIPTION_LENGTH) {
      description = description.substring(
        CONST.MIN_DESCRIPTION_LENGTH,
        CONST.MAX_DESCRIPTION_LENGTH
      );
      return (
        <p>
          {description}... 
          <span
            role="button"
            tabIndex={0}
            onClick={this.openGlossary}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                this.openGlossary();
              }
            }}>
            read more
            </span>
        </p>
      );
    } else {
      return <p>{description}</p>;
    }
  };

  onScroll = () => {
    this.setState({isOnScrollMode: true});
    this.updateTooltipDragPosition(this.props.clickedContent);
  };

  renderTooltipContent = () => {
    if (
      this.props.tooltipApi.error === false &&
      this.props.tooltipApi.loading === true
    )
      return <GlossaryDetailsLoader />;
    else if (
      this.props.tooltipApi.error === false &&
      this.props.tooltipApi.loading === false
    )
      return (
        <>
          {this.renderImage()}
          {this.renderTitle()}
          {this.renderDescription()}
        </>
      );
    else if (
      this.props.tooltipApi.error === true &&
      this.props.tooltipApi.loading === false
    )
      return <GlossaryErrorState />;
  };

  render() {
    return (
      <div style={this.state.style} className={this.state.classList.join(' ')}>
        <div className={style.tooltip_header}>
          <Image
            onMouseDown={this.onDragStart}
            src={MoveIcon}
            alt='move tooltip card'
          />
          <CloseButton close={this.closeTooltip} />
        </div>
        <div className={style.tooltip_body}>{this.renderTooltipContent()}</div>
      </div>
    );
  }
}

HighlightModal.propTypes = {
  /**
   * String repersenting description showed on the modal window
   */
  description: PropTypes.string,
  /**
   * String repersenting title showed on the modal window
   */
  title: PropTypes.string,
  /**
   * Url to the image.
   */
  image: PropTypes.string.isRequired,
  /**
   * String repersenting classname of the image
   */
  className: PropTypes.string,
  /**
   * Function to close the tooltip
   */
  close: PropTypes.func,
  /**
   * Function to open the Glossary component after clicking read more on description
   */
  openGlossary: PropTypes.func.isRequired,
  /**
   * String repersenting classname of the tooptip
   */
  clickedContent: PropTypes.shape({
    /**
     * Number repersenting left postion of tooltip modal
     */
    left: PropTypes.string,
    /**
     * Number repersenting top postion of tooltip modal
     */
    top: PropTypes.string,
    /**
     * Stirng repersenting text of heighlighted content
     */
    highLightText: PropTypes.string,
    /**
     * Stirng repersenting text of heighlighted content
     */
    highLightTextHeight: PropTypes.string,
    /**
     * Stirng repersenting arro
     */
    arrowPosition: PropTypes.oneOf([
      'TOP_LEFT',
      'TOP_RIGHT',
      'BOTTOM_LEFT',
      'BOTTOM_RIGHT',
      'LEFT_BOTTOM',
      'LEFT_CENTRE',
      'RIGHT_CENTRE',
      'RIGHT_BOTTOM',
    ]),
  }).isRequired,
};

HighlightModal.defaultProps = {
  image: '',
  title: '',
  description: '',
  className: '',
};
